// Utility functions for sharing functionality

/**
 * Copies text to clipboard using the modern Clipboard API with fallback
 * @param text - The text to copy to clipboard
 * @returns Promise that resolves to true if successful, false otherwise
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    // Modern Clipboard API (preferred)
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Fallback for older browsers or non-secure contexts
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    return successful;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

/**
 * Generates a shareable URL for a product
 * @param productName - The name of the product
 * @param baseUrl - Optional base URL (defaults to current origin)
 * @returns The shareable product URL
 */
export const generateProductUrl = (productName: string, baseUrl?: string): string => {
  const base = baseUrl || window.location.origin;
  const productSlug = productName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  
  return `${base}/products/${productSlug}`;
};

/**
 * Copies a product link to clipboard and shows user feedback
 * @param productName - The name of the product to share
 * @param productUrl - Optional direct product URL from backend
 * @param onSuccess - Optional callback for successful copy
 * @param onError - Optional callback for failed copy
 */
export const shareProduct = async (
  productName: string,
  productUrl?: string,
  onSuccess?: (url: string) => void,
  onError?: (error: string) => void
): Promise<void> => {
  // Use provided URL or generate one from product name
  const urlToShare = productUrl || generateProductUrl(productName);
  const success = await copyToClipboard(urlToShare);
  
  if (success) {
    onSuccess?.(urlToShare);
  } else {
    onError?.('Failed to copy link to clipboard');
  }
};
